// The Build HTTP wire contract (Slice 5; slice-plan.md:79-81; D34 app-stage lifecycle).
//
// The SINGLE source of truth for the Build routes that apps/api (producer) and apps/web (consumer)
// build against - pinned here so the two sides cannot drift. Success payloads are the domain schemas
// from ../model/build.js; this file adds the path params, request bodies, and the route path strings
// the api lane consumes verbatim. Every failure returns the shared `ErrorEnvelope { code, message }`
// (http/knowledge.ts:21); a `not_found`/`forbidden` on an app that is not the caller's returns a
// byte-identical not-found (app-security §2 A01 - existence is not revealed). Guided workflow validation
// (D35) is a build-phase gate and lives here (D5 reconciliation).
import { z } from "zod";
import { AppId } from "../ids.js";
import { NonEmptyString } from "../model/common.js";
import { Cloud } from "../model/enums.js";
import { BuildRequest, BuildResult, WorkflowValidation } from "../model/build.js";
/** The canonical route path strings, for the api lane to register verbatim (D2; `/v1/apps/:appId/...` idiom). */
export const BUILD_ROUTES = {
    workflowValidation: "/v1/apps/:appId/build/workflow-validation",
    build: "/v1/apps/:appId/build",
    buildById: "/v1/apps/:appId/build/:buildId",
    promote: "/v1/apps/:appId/build/promote",
};
/** Path params for the app-scoped Build routes: the one app the request is scoped to. */
export const BuildRouteParams = z.object({ appId: AppId });
/** Path params for the build-status read: the app + the build id. */
export const BuildByIdParams = z.object({ appId: AppId, buildId: NonEmptyString });
// --- Workflow validation: POST (submit) / GET (read) /v1/apps/:appId/build/workflow-validation (D35) ---
/** POST body: the founder's confirmations across the app's own workflows (D35). */
export const WorkflowValidationRequest = WorkflowValidation;
/** 200 -> the stored `WorkflowValidation`, else an `ErrorEnvelope`. */
export const WorkflowValidationResponse = WorkflowValidation;
// --- Build: POST /v1/apps/:appId/build (MVP or Production build, D34) ---
/**
 * POST body: the stage to build and the target cloud (`BuildRequest`). The body echoes `appId` because
 * the port needs it, but the ROUTE path `:appId` is authoritative on any conflict (model/build.ts:BuildRequest).
 */
export const BuildRequestBody = BuildRequest;
/** 200 -> the `BuildResult` (hard gate | manual step | entitlement refusal | building | built), else an `ErrorEnvelope`. */
export const BuildResponse = BuildResult;
// --- Build status: GET /v1/apps/:appId/build/:buildId ---
/** 200 -> the current `BuildResult` for a build, else an `ErrorEnvelope`. */
export const BuildStatusResponse = BuildResult;
// --- Promote: POST /v1/apps/:appId/build/promote (mvp -> production, D34; slice-plan.md:80) ---
/** POST body: the production cloud to promote onto (`Cloud`, enums.ts:82). */
export const PromoteRequest = z.object({ cloud: Cloud });
/** 200 -> the `BuildResult` of the promotion (built{newStage:"production"} or a gate/refusal), else an `ErrorEnvelope`. */
export const PromoteResponse = BuildResult;
//# sourceMappingURL=build.js.map